import { routeAwardMedal, routeMilestoneCopy } from "@healthos/client-data";
import type { api } from "@healthos/schemas";
import { Button, Modal, ModalDescription, ModalTitle } from "@healthos/ui";
import { CheckCircle, FlagCheckered, MapPin, UsersThree } from "@phosphor-icons/react";
import { useDistanceUnit } from "../hooks/use-distance-unit";
import { formatDistance } from "../lib/distance-units";
import { MedalViewer } from "./awards/medal-viewer";

/** A shared award celebration with the same medal as the collection. */
export function RouteMilestoneModal({
  open,
  onOpenChange,
  celebration,
  viewerFirstName,
  onShare,
  onViewAwards,
  sharing = false,
  shared = false,
  shareError = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  celebration: api.RouteMilestoneCelebration;
  viewerFirstName?: string | null;
  onShare?: () => void;
  onViewAwards?: () => void;
  sharing?: boolean;
  shared?: boolean;
  shareError?: boolean;
}) {
  const unit = useDistanceUnit();
  const copy = routeMilestoneCopy(celebration, { viewerFirstName: viewerFirstName ?? null });
  const isGroup = celebration.subject === "group";
  const isFinish = celebration.kind === "completion";

  return (
    <Modal open={open} onOpenChange={onOpenChange} size="lg" contentClassName="overflow-hidden">
      <div className="flex flex-col items-center px-6 pb-9 pt-9 text-center sm:px-10 sm:pb-10 sm:pt-10">
        <p className="flex items-center gap-1.5 font-mono text-2xs font-semibold uppercase tracking-[0.16em] text-ink-brand motion-safe:animate-[fade-in-up_260ms_var(--ease-out-expo)_120ms_both]">
          {isFinish ? (
            <FlagCheckered size={13} weight="fill" aria-hidden="true" />
          ) : isGroup ? (
            <UsersThree size={13} weight="fill" aria-hidden="true" />
          ) : (
            <MapPin size={13} weight="fill" aria-hidden="true" />
          )}
          {copy.eyebrow}
        </p>

        <ModalTitle asChild>
          <h2 className="mt-3 max-w-[22ch] text-balance font-title text-3xl font-medium leading-[1.08] tracking-[-0.02em] text-navy motion-safe:animate-[fade-in-up_280ms_var(--ease-out-expo)_200ms_both]">
            {copy.subject} {isFinish ? "completed" : "reached"}{" "}
            <span className="font-normal italic text-ink-brand">{copy.title}</span>
          </h2>
        </ModalTitle>

        <ModalDescription asChild>
          <p className="mt-3 max-w-[42ch] text-base leading-relaxed text-ink-secondary motion-safe:animate-[fade-in-up_280ms_var(--ease-out-expo)_280ms_both]">
            {copy.description}
          </p>
        </ModalDescription>

        <div className="mt-5 w-full rounded-2xl bg-surface-secondary">
          <MedalViewer
            {...routeAwardMedal(celebration)}
            title={isFinish ? celebration.routeName : celebration.milestoneTitle}
            interactive={false}
            className="h-72"
          />
        </div>
        <p className="mt-3 text-sm text-ink-brand">
          {isFinish ? "Finisher award earned" : "Waypoint award earned"}
        </p>
        {onViewAwards ? (
          <Button variant="secondary" className="mt-4" onClick={onViewAwards}>
            View all awards
          </Button>
        ) : (
          <a href="/challenges/awards" className="mt-4 text-sm font-semibold text-ink-brand">
            View all awards
          </a>
        )}

        <dl className="mt-5 grid w-full grid-cols-2 divide-x divide-border-subtle border-y border-border-subtle py-4 motion-safe:animate-[fade-in-up_320ms_var(--ease-out-expo)_460ms_both]">
          <Stat
            value={copy.standing.split(" on ")[0] ?? copy.standing}
            label={isFinish ? "walked" : "waypoint"}
          />
          <Stat
            value={
              isFinish && celebration.distanceMeters
                ? formatDistance(celebration.distanceMeters, unit, { maximumFractionDigits: 0 })
                : copy.remainingMeters === null
                  ? copy.progress
                  : `${formatDistance(copy.remainingMeters, unit, { maximumFractionDigits: 0 })} to go`
            }
            label={
              isFinish
                ? "end to end"
                : copy.remainingMeters === null
                  ? "of the route"
                  : "still ahead"
            }
          />
        </dl>

        <p className="mt-3 max-w-[42ch] text-xs text-ink-tertiary motion-safe:animate-[fade-in-up_260ms_var(--ease-out-expo)_520ms_both]">
          {copy.progress} · {celebration.routeName}
        </p>

        {onShare ? (
          <div className="mt-5 flex w-full flex-col items-center motion-safe:animate-[fade-in-up_260ms_var(--ease-out-expo)_600ms_both]">
            <button
              type="button"
              onClick={onShare}
              disabled={sharing || shared}
              className="group inline-flex items-center gap-2 rounded-lg px-1 py-1 text-sm font-medium text-ink-secondary transition-colors hover:text-ink-heading disabled:cursor-not-allowed disabled:opacity-50"
            >
              {shared ? (
                <>
                  <CheckCircle
                    size={17}
                    weight="fill"
                    className="text-teal-700"
                    aria-hidden="true"
                  />
                  Shared with your community
                </>
              ) : (
                <>
                  <UsersThree
                    size={17}
                    weight="duotone"
                    className="text-ink-tertiary transition-colors group-hover:text-ink-secondary"
                    aria-hidden="true"
                  />
                  {sharing ? "Sharing" : "Share with your community"}
                </>
              )}
            </button>
            {!shared && !shareError ? (
              <p className="m-0 mt-1 text-xs text-ink-tertiary">
                {isFinish ? "Route and completion only" : "Route, waypoint, and progress only"}
              </p>
            ) : null}
            {shareError ? (
              <p role="status" className="m-0 mt-2 text-xs text-attention-urgent-ink">
                We could not share that just yet. Please try again.
              </p>
            ) : null}
          </div>
        ) : null}

        <Button
          onClick={() => onOpenChange(false)}
          className="mt-6 motion-safe:animate-[fade-in-up_260ms_var(--ease-out-expo)_680ms_both]"
        >
          {isFinish ? "Choose the next route" : "Keep walking"}
        </Button>
      </div>
    </Modal>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex min-w-0 flex-col items-center gap-1 px-2">
      <dd className="text-balance font-sans text-xl font-medium leading-tight text-ink-heading">
        {value}
      </dd>
      <dt className="text-balance font-mono text-2xs font-semibold uppercase tracking-[0.12em] text-ink-tertiary">
        {label}
      </dt>
    </div>
  );
}
